import React from 'react';
import { ApolloError } from '@apollo/client';
import { Menu, MenuItem } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';

import {
  IconButton,
  LinearProgress,
  LoadedContent,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MoreVertIcon,
} from '@occc-app/design-system';
import { LocationGroupFragment } from '@occc/graphql';
import { IPaginationProps } from '@occc-app/hooks/usePaginatedQuery';

import { AFTER_PARAM_NAME } from './constants';

import { routeCreator } from '../utils';

type LocationGroupsTableProps = {
  locationGroups: LocationGroupFragment[] | undefined | null;
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  onEdit: (group: LocationGroupFragment) => void;
  onDelete: (group: LocationGroupFragment) => void;
  isUserPermittedToEditLocationGroup: boolean;
  isUserPermittedToRemoveLocationGroup: boolean;
};

export const LocationGroupsTable: React.FC<LocationGroupsTableProps> = ({
  locationGroups,
  loading,
  error,
  paginationProps,
  isUserPermittedToEditLocationGroup,
  isUserPermittedToRemoveLocationGroup,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();

  const [actionsForUuid, setActionsForUuid] = React.useState<string | null>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const getHandlerForAdditionalActions =
    (group: LocationGroupFragment) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(group.uuid);
    };

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const areActionsVisible =
    isUserPermittedToEditLocationGroup || isUserPermittedToRemoveLocationGroup;

  const renderData = (items: LocationGroupFragment[]) => {
    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Group Name</TableCell>
              <TableCell>Locations</TableCell>
              {areActionsVisible && <TableCell></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.uuid}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(routeCreator.locationGroupDetails(item.uuid));
                }}
              >
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.countOfLocations}</TableCell>
                {areActionsVisible && (
                  <TableCell align='right' width='10%'>
                    <>
                      <IconButton onClick={getHandlerForAdditionalActions(item)}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        open={item.uuid === actionsForUuid}
                        onClose={(event) => {
                          (event as any).stopPropagation();
                          handleClose();
                        }}
                        anchorEl={anchorEl}
                        PaperProps={{
                          sx: {
                            borderRadius: '4px',
                          },
                        }}
                      >
                        <MenuItem
                          component={Link}
                          to={routeCreator.locationGroupDetails(item.uuid)}
                        >
                          View Group Details
                        </MenuItem>
                        {isUserPermittedToEditLocationGroup && (
                          <MenuItem
                            onClick={(event) => {
                              event.stopPropagation();

                              onEdit(item);
                              handleClose();
                            }}
                          >
                            Rename Group
                          </MenuItem>
                        )}

                        {isUserPermittedToRemoveLocationGroup && (
                          <MenuItem
                            onClick={(event) => {
                              event.stopPropagation();

                              onDelete(item);
                              handleClose();
                            }}
                          >
                            Delete Group
                          </MenuItem>
                        )}
                      </Menu>
                    </>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<LocationGroupFragment>
      error={error}
      loading={loading}
      items={locationGroups}
      renderData={renderData}
      afterParamName={AFTER_PARAM_NAME}
    />
  );
};
